



import { useState, useEffect, useRef } from "react";
import { createUserApi, getDepartmentsApi } from "../../app/api";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Briefcase,
  Check,
  ChevronDown,
  ShieldCheck,
  Search,
  Users,
  UserPlus,
  RotateCcw,
  Plus
} from "lucide-react";

const ROLES = [
  { id: 3, label: "Admin" },
  { id: 6, label: "User" },
];

const GENDERS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

export default function CreateUser() {
  const [form, setForm] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    job_title: "",
    role_id: "",
    gender: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await getDepartmentsApi();
      if (res?.success) setDepartments(res.data || []);
    })();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleReset = () => {
    setForm({
      email: "",
      password: "",
      first_name: "",
      last_name: "",
      job_title: "",
      role_id: "",
      gender: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    const res = await createUserApi({
      ...form,
      role_id: Number(form.role_id),
    });

    if (res.success) {
      toast.success("User created successfully!");
      handleReset();
    } else {
      toast.error(res.message || "Failed to create user");
    }
    setIsSubmitting(false);
  };

  return (
    <div className="max-w-7xl mx-auto md:mt-20">
      <div className="
        relative
        overflow-hidden
        rounded-lg
        p-8
        bg-[#12082F]
        backdrop-blur-2xl
        text-white
        border border-white/10
        shadow-[0_40px_100px_-20px_rgba(0,0,0,0.7)]
      ">
        {/* Decorative background blurs */}
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-purple-600/20 blur-[100px] rounded-full pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-blue-600/20 blur-[100px] rounded-full pointer-events-none" />

        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-8 ">
            <div className="p-3 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-600 shadow-lg md:mt-[-1px] mt-[-14px]">
              <UserPlus className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-3xl whitespace-nowrap ">Create New User</h3>
              <p className="text-white/50 text-sm font-medium">Register a new employee to the system</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

              {/* First Name */}
              <Field label="First Name" icon={<User className="w-4 h-4" />}>
                <input
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                  placeholder="Enter First Name"
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-11 py-3 text-sm outline-none transition focus:border-purple-500/50 focus:bg-white/[0.08]"
                />
              </Field>

              {/* Last Name */}
              <Field label="Last Name" icon={<User className="w-4 h-4" />}>
                <input
                  name="last_name"
                  value={form.last_name}
                  onChange={handleChange}
                  placeholder="Enter  Last Name"
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-11 py-3 text-sm outline-none transition focus:border-purple-500/50 focus:bg-white/[0.08]"
                />
              </Field>

              {/* Email */}
              <Field label="Email Address" icon={<Mail className="w-4 h-4" />}>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Enter your Email"
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-11 py-3 text-sm outline-none transition focus:border-purple-500/50 focus:bg-white/[0.08]"
                />
              </Field>

              {/* Password */}
              <Field label="Security Password" icon={<Lock className="w-4 h-4" />}>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="Enter password"
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-11 py-3 text-sm outline-none transition focus:border-purple-500/50 focus:bg-white/[0.08]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white transition p-1"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </Field>

              {/* Gender */}
              <Field label="Gender Selection" icon={<Users className="w-4 h-4" />}>
                <FormSelect
                  name="gender"
                  value={form.gender}
                  onChange={handleChange}
                  placeholder="Select Gender"
                  options={GENDERS}
                />
              </Field>

              {/* Department */}
              <Field label="Department" icon={<Briefcase className="w-4 h-4" />}>
                <FormSelect
                  name="job_title"
                  value={form.job_title}
                  onChange={handleChange}
                  placeholder="Select Department"
                  searchable
                  options={departments.map((department) => ({
                    value: department.name,
                    label: department.name,
                  }))}
                />
              </Field>

              {/* Role */}
              <Field label="Access Level / Role" icon={<ShieldCheck className="w-4 h-4" />}>
                <FormSelect
                  name="role_id"
                  value={form.role_id}
                  onChange={handleChange}
                  placeholder="Select User Role"
                  options={ROLES.map((role) => ({
                    value: String(role.id),
                    label: role.label,
                  }))}
                />
              </Field>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-4 mt-8 pt-6 border-t border-white/5">
              <button
                type="button"
                onClick={handleReset}
                className="
                  flex items-center justify-center gap-2
                  px-8 py-3 rounded-lg
                  bg-white/5 border border-white/10
                  hover:bg-white/10 transition-all font-semibold text-sm
                "
              >
                <RotateCcw className="w-4 h-4" />
                Reset Form
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className="
                  flex items-center justify-center gap-2
                  px-10 py-3 rounded-2xl
                  bg-gradient-to-r from-purple-500 to-blue-600
                  hover:from-purple-600 hover:to-blue-700
                  transition-all shadow-xl shadow-purple-500/20
                  font-bold text-sm
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-lg animate-spin" />
                ) : (
                  <Plus className="w-5 h-5 text-white" />
                )}
                Create User
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon, children }) {
  return (
    <div className="flex flex-col gap-2 group">
      <label className="text-[11px] font-bold uppercase tracking-widest text-white/40 ml-1">
        {label}
      </label>
      <div className="relative">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 transition group-focus-within:text-purple-400">
          {icon}
        </div>
        {children}
      </div>
    </div>
  );
}

function FormSelect({
  name,
  value,
  onChange,
  placeholder,
  options,
  searchable = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuPosition, setMenuPosition] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const selectedOption = options.find(
    (option) => String(option.value) === String(value)
  );
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(query.trim().toLowerCase())
  );

  const updateMenuPosition = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const width = Math.max(rect.width, 300);
    const left = Math.min(
      Math.max(12, rect.left),
      window.innerWidth - width - 12
    );

    setMenuPosition({
      top: rect.bottom + 8,
      left,
      width,
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen]);

  const selectOption = (optionValue) => {
    onChange({
      target: {
        name,
        value: optionValue,
      },
    });
    setIsOpen(false);
    setQuery("");
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          updateMenuPosition();
          setIsOpen((current) => !current);
        }}
        className="flex w-full items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-11 py-3 text-left text-sm text-white outline-none transition hover:border-purple-300/35 hover:bg-white/[0.08] focus:border-purple-400/60 focus:ring-2 focus:ring-purple-400/20"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span
          className={`min-w-0 truncate ${
            selectedOption ? "text-white" : "text-white/42"
          }`}
        >
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-white/45 transition ${
            isOpen ? "rotate-180 text-cyan-200" : ""
          }`}
        />
      </button>

      {isOpen &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            className="fixed z-[9999] overflow-hidden rounded-2xl border border-cyan-200/20 bg-[#100a22] p-2 text-white shadow-[0_24px_70px_rgba(0,0,0,0.55)]"
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
              width: menuPosition.width,
            }}
          >
            {searchable && (
              <div className="relative mb-2">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
                <input
                  autoFocus
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search department..."
                  className="w-full rounded-xl border border-white/10 bg-white/8 py-2.5 pl-9 pr-3 text-sm text-white outline-none placeholder:text-white/35 focus:border-cyan-200/45"
                />
              </div>
            )}

            <div className="max-h-64 overflow-y-auto pr-1">
              {filteredOptions.length === 0 ? (
                <div className="rounded-xl px-3 py-4 text-center text-sm text-white/45">
                  No options found
                </div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = String(option.value) === String(value);

                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => selectOption(option.value)}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition ${
                        isSelected
                          ? "bg-cyan-400/16 text-cyan-100"
                          : "text-white/82 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <span
                        className={`grid h-8 w-8 shrink-0 place-items-center rounded-xl border ${
                          isSelected
                            ? "border-cyan-200/45 bg-cyan-300/18"
                            : "border-white/10 bg-white/6"
                        }`}
                      >
                        {isSelected ? (
                          <Check className="h-4 w-4 text-cyan-200" />
                        ) : (
                          <span className="h-2 w-2 rounded-full bg-white/35" />
                        )}
                      </span>
                      <span className="min-w-0 flex-1 truncate font-medium">
                        {option.label}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}




// import { useState, useEffect } from "react";
// import { createUserApi, getDepartmentsApi } from "../../app/api";
// import toast from "react-hot-toast";

// const ROLES = [
//   { id: 3, label: "Admin" },
//   { id: 6, label: "User" },
// ];

// export default function CreateUser() {
//   const [form, setForm] = useState({
//     email: "",
//     password: "",
//     first_name: "",
//     last_name: "",
//     job_title: "",
//     role_id: "",
//   });

//   const [departments, setDepartments] = useState([]);

//   useEffect(() => {
//     (async () => {
//       const res = await getDepartmentsApi();
//       if (res?.success) setDepartments(res.data || []);
//     })();
//   }, []);

//   const handleChange = (e) => {
//     setForm({ ...form, [e.target.name]: e.target.value });
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     const res = await createUserApi({
//       ...form,
//       role_id: Number(form.role_id),
//     });

//     if (res.success) {
//       toast.success("User created");
//       setForm({
//         email: "",
//         password: "",
//         first_name: "",
//         last_name: "",
//         job_title: "",
//         role_id: "",
//       });
//     } else {
//       toast.error(res.message || "Failed");
//     }
//   };

//   return (
//     <div className="
//       rounded-2xl
//       p-5
//       md:mt-20
//       bg-gradient-to-br
//       from-[#2d1c6a]
//       via-[#24155a]
//       to-[#12082f]
//       text-white
//       border border-white/10
//       shadow-2xl shadow-[#12082F]/60
//       relative
//     ">

//       {/* glow */}
//       <div className="absolute -top-10 -right-10 w-60 h-60 bg-purple-500/20 blur-3xl rounded-full" />
//       <div className="absolute bottom-0 -left-10 w-60 h-60 bg-fuchsia-500/20 blur-3xl rounded-full" />

//       <h3 className="font-bold text-2xl mb-4 relative z-10">
//         Create New User
//       </h3>

//       <form
//         onSubmit={handleSubmit}
//         className="grid grid-cols-1 md:grid-cols-2 gap-4 relative z-10"
//       >
//         <Field label="Email">
//           <input
//             name="email"
//             value={form.email}
//             onChange={handleChange}
//             className="input-dark shadow-lg"
//           />
//         </Field>

//         <Field label="Password">
//           <input
//             type="password"
//             name="password"
//             value={form.password}
//             onChange={handleChange}
//             className="input-dark shadow-lg"
//           />
//         </Field>

//         <Field label="First Name">
//           <input
//             name="first_name"
//             value={form.first_name}
//             onChange={handleChange}
//             className="input-dark shadow-lg"
//           />
//         </Field>

//         <Field label="Last Name">
//           <input
//             name="last_name"
//             value={form.last_name}
//             onChange={handleChange}
//             className="input-dark shadow-lg"
//           />
//         </Field>

//         <Field label="Department">
//           <select
//             name="job_title"
//             value={form.job_title}
//             onChange={handleChange}
//             className="input-dark shadow-lg"
//           >
//             <option value="">Select department</option>
//             {departments.map((d) => (
//               <option key={d.id} value={d.name} className="text-black shadow-lg">
//                 {d.name}
//               </option>
//             ))}
//           </select>
//         </Field>

//         <Field label="Role">
//           <select
//             name="role_id"
//             value={form.role_id}
//             onChange={handleChange}
//             className="input-dark"
//           >
//             <option value="">Select role</option>
//             {ROLES.map((r) => (
//               <option key={r.id} value={r.id} className="text-black">
//                 {r.label}
//               </option>
//             ))}
//           </select>
//         </Field>

//         <div className="md:col-span-2 flex justify-end gap-3 mt-2">
//           <button
//             type="reset"
//             className="
//               px-4 py-1 rounded-lg
//               bg-white/10 border border-white/20
//               hover:bg-white/20 text-sm
//             "
//           >
//             Reset
//           </button>

//           <button
//             type="submit"
//             className="
//               px-4 py-1 rounded-lg
//               bg-green-500 hover:bg-green-600
//               text-black text-sm font-medium
//             "
//           >
//             Create User
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// }

// function Field({ label, children }) {
//   return (
//     <div className="flex flex-col gap-1 shadow-lg">
//       <label className="text-xs text-white/70 shadow-lg">
//         {label}
//       </label>
//       {children}
//     </div>
//   );
// }
